import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, Button, Select, Input, useEinsatz, useConnectionRetry, useBestaetigung } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { AlertTriangle, CheckCircle2, ChevronDown, CircleDashed, ExternalLink, Info, ListChecks, Lock, LockOpen, Printer, ShieldAlert, ShieldCheck, ShieldX, Trash2 } from 'lucide-react'
import { bewerte, STANDARD, type Eingaben, type Ergebnis } from '../components/risikobewertung'

// Einsatzbezogene Risikobewertung nach EGRED 2 (Kap. 4.2.2/4.2.3) mit BOS-Standardszenarien (STS-BOS).
// Die Regeln stehen in ../components/risikobewertung.ts; gespeichert wird je Einsatz über die Führungs-API.

interface EinsatzOption { id: number; titel: string; status: string }
interface DrohneOption {
  id: number; label: string
  groesse_klasse: Eingaben['groesse'] | null; groesse_quelle: Quelle
  eu_klasse: Eingaben['klasse'] | null; eu_klasse_quelle: Quelle
  startmasse_g: number | null; startmasse_quelle: Quelle
  katalog_typ: string | null; katalog_quelle: string | null
}
type Quelle = 'drohne' | 'katalog' | null
const QUELLE: Record<'drohne' | 'katalog', string> = { drohne: 'Stammdaten der Drohne', katalog: 'Drohnenkatalog' }

/** Felder unter „1. Fluggerät“, die aus den Stammdaten der Drohne kommen */
type GeraeteFeld = 'groesse' | 'mtomAb25' | 'klasse' | 'hangar'
type Vorgaben = Partial<Pick<Eingaben, GeraeteFeld | 'unter250'>>

/** Bekannte Angaben einer Drohne für die Risikobewertung (Admin → Drohnen → Angaben für die Risikobewertung) */
function geraeteVorgaben(d: DrohneOption): Vorgaben {
  const v: Vorgaben = {}
  if (d.groesse_klasse) v.groesse = d.groesse_klasse
  if (d.startmasse_g) { v.mtomAb25 = d.startmasse_g >= 25000; v.unter250 = d.startmasse_g < 250 }
  if (d.eu_klasse) v.klasse = d.eu_klasse
  if (d.katalog_typ) v.hangar = /automatisiert|dock/i.test(d.katalog_typ)
  return v
}

/** Schloss rechts neben einer Frage: gesperrt = Wert aus den Stammdaten der Drohne */
function Schloss({ gesperrt, quelle, onEntsperren }: { gesperrt: boolean; quelle: string; onEntsperren: () => void }) {
  if (!gesperrt) return null
  const kurz = /katalog/i.test(quelle) ? 'aus Katalog' : 'aus Drohne'
  return (
    <button type="button" onClick={onEntsperren} title={`${quelle} – zum Ändern entsperren`} aria-label={`${quelle}, zum Ändern entsperren`}
      className="ml-auto flex h-9 shrink-0 items-center gap-1 rounded-lg px-2 text-xs text-primary-300 hover:bg-primary-400/10">
      <Lock className="h-4 w-4" /><span className="hidden sm:inline">{kurz}</span>
    </button>
  )
}
interface Gespeichert {
  id: number; einsatz_id: number | null; einsatz_titel: string | null; drohne: string | null
  erstellt_am: string | null; erstellt_von: string | null; zweite_person: string | null
  bemerkung: string | null; ampel: Ergebnis['ampel'] | null; titel: string | null
}

const AMPEL = {
  gruen: { rahmen: 'border-emerald-500/50', kopf: 'bg-emerald-500/15 text-emerald-200', icon: ShieldCheck, wort: 'Flug möglich' },
  gelb: { rahmen: 'border-amber-500/50', kopf: 'bg-amber-500/15 text-amber-100', icon: ShieldAlert, wort: 'Vor dem Flug erfüllen' },
  rot: { rahmen: 'border-red-500/50', kopf: 'bg-red-500/15 text-red-200', icon: ShieldX, wort: 'Kein Standardfall' },
} as const

const fmtDT = (s: string | null) => (s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–')

/** Auswahl als große Schaltflächen (Tablet-tauglich) */
function Wahl<T extends string>({ label, wert, optionen, onChange, hinweis, gesperrt = false, quelle = '', onEntsperren }: {
  label: string; wert: T; onChange: (v: T) => void; hinweis?: string
  optionen: Array<{ value: T; label: string; info?: string }>
  gesperrt?: boolean; quelle?: string; onEntsperren?: () => void
}) {
  return (
    <fieldset className="space-y-1.5">
      <div className="flex items-center gap-2">
        <legend className="text-sm font-medium text-dark-text">{label}</legend>
        <Schloss gesperrt={gesperrt} quelle={quelle} onEntsperren={() => onEntsperren?.()} />
      </div>
      {hinweis && <p className="text-xs text-dark-text-subtle">{hinweis}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {optionen.map(o => (
          <button key={o.value} type="button" aria-pressed={wert === o.value} onClick={() => onChange(o.value)} disabled={gesperrt}
            className={`min-h-[48px] rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default ${wert === o.value
              ? 'border-primary-500 bg-primary-600/15 text-dark-text ring-1 ring-primary-500/40'
              : gesperrt ? 'border-dark-border text-dark-text-subtle opacity-50' : 'border-dark-border text-dark-text-muted hover:border-primary-500/50 hover:text-dark-text'}`}>
            <span className="block font-medium">{o.label}</span>
            {o.info && <span className="mt-0.5 block text-xs text-dark-text-subtle">{o.info}</span>}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

/** Ja/Nein-Frage als Kontrollkästchen mit Erklärung; children stehen unter der Frage (außerhalb des Labels) */
function Haken({ label, info, wert, onChange, children, gesperrt = false, quelle = '', onEntsperren }: {
  label: string; info?: string; wert: boolean; onChange: (v: boolean) => void; children?: React.ReactNode
  gesperrt?: boolean; quelle?: string; onEntsperren?: () => void
}) {
  return (
    <div className="rounded-lg border border-dark-border hover:border-primary-500/50">
      <div className="flex items-start">
        <label className={`flex min-h-[48px] min-w-0 flex-1 items-start gap-3 px-3 py-2.5 ${gesperrt ? 'cursor-default' : 'cursor-pointer'}`}>
          <input type="checkbox" checked={wert} disabled={gesperrt} onChange={e => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 rounded accent-primary-500" />
          <span className="min-w-0">
            <span className="block text-sm text-dark-text">{label}</span>
            {info && <span className="mt-0.5 block text-xs text-dark-text-subtle">{info}</span>}
          </span>
        </label>
        {gesperrt && <div className="pr-2 pt-1.5"><Schloss gesperrt quelle={quelle} onEntsperren={() => onEntsperren?.()} /></div>}
      </div>
      {children && <div className="flex flex-wrap gap-2 px-3 pb-3 pl-11">{children}</div>}
    </div>
  )
}

/** Mustersicherheitseinweisung (EGRED 2 / STS-BOS) als PDF, erzeugt mit scripts/erzeuge_sicherheitseinweisung.py */
const EINWEISUNG_PDF = '/sicherheitseinweisung-drohneneinsatz.pdf'

/** PDF über einen unsichtbaren Rahmen drucken; geht das nicht (z. B. iPad), im neuen Tab öffnen */
function pdfDrucken(url: string) {
  const rahmen = document.createElement('iframe')
  rahmen.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  rahmen.src = url
  rahmen.onload = () => {
    try { rahmen.contentWindow?.focus(); rahmen.contentWindow?.print() }
    catch { window.open(url, '_blank', 'noopener') }
    setTimeout(() => rahmen.remove(), 60000)
  }
  document.body.appendChild(rahmen)
}

const knopf = 'inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-dark-border px-3 text-sm text-dark-text-muted hover:border-primary-500/60 hover:text-dark-text'

/** Abschnitt 1–7, über den Pfeil oben rechts ein- und ausklappbar */
function Abschnitt({ nr, titel, children, zusatz }: { nr: number; titel: string; children: React.ReactNode; zusatz?: React.ReactNode }) {
  const [offen, setOffen] = useState(true)
  const id = `rb-abschnitt-${nr}`
  return (
    <Card className="p-4 sm:p-5">
      <h2 className="flex items-center gap-2 text-base font-semibold text-dark-text">
        <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen} aria-controls={id}
          className="flex min-h-[40px] min-w-0 flex-1 items-center gap-2 text-left">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-dark-bg-tertiary text-xs text-dark-text-muted">{nr}</span>
          <span className="min-w-0 flex-1">{titel}</span>
          {zusatz}
          <ChevronDown className={`h-5 w-5 shrink-0 text-dark-text-muted transition-transform ${offen ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </h2>
      {offen && <div id={id} className="mt-4 space-y-4">{children}</div>}
    </Card>
  )
}

function Liste({ titel, punkte, art }: { titel: string; punkte: string[]; art: 'ok' | 'offen' | 'aus' | 'info' | 'regel' }) {
  if (!punkte.length) return null
  const Icon = art === 'ok' ? CheckCircle2 : art === 'offen' ? CircleDashed : art === 'aus' ? AlertTriangle : art === 'regel' ? ListChecks : Info
  const farbe = art === 'ok' ? 'text-emerald-400' : art === 'offen' ? 'text-amber-400' : art === 'aus' ? 'text-red-400' : art === 'regel' ? 'text-dark-text-muted' : 'text-sky-400'
  return (
    <div>
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">{titel}</h3>
      <ul className="space-y-1.5">
        {punkte.map(p => (
          <li key={p} className="flex gap-2 text-sm text-dark-text"><Icon className={`mt-0.5 h-4 w-4 shrink-0 ${farbe}`} /><span>{p}</span></li>
        ))}
      </ul>
    </div>
  )
}

function ErgebnisKarte({ erg }: { erg: Ergebnis }) {
  const a = AMPEL[erg.ampel]
  const Icon = a.icon
  const zeigeSora = !erg.szenario && erg.kategorie !== 'offen'
  return (
    <Card className={`overflow-hidden border-2 p-0 ${a.rahmen}`}>
      <div className={`flex items-start gap-3 px-4 py-3 ${a.kopf}`} role="status" aria-live="polite">
        <Icon className="mt-0.5 h-6 w-6 shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{a.wort}</div>
          <div className="text-base font-semibold leading-snug">{erg.titel}</div>
        </div>
      </div>
      <div className="space-y-4 px-4 py-4">
        <p className="text-sm text-dark-text">{erg.zusammenfassung}</p>
        <Liste titel="Ausschlussgründe" punkte={erg.ausschluss} art="aus" />
        <Liste titel="Noch zu erfüllen" punkte={erg.fehlend} art="offen" />
        <Liste titel="Erfüllt" punkte={erg.erfuellt ?? []} art="ok" />
        <Liste titel={erg.ampel === 'rot' ? 'Vorgehen' : 'Im Betrieb einzuhalten'} punkte={erg.auflagen} art="regel" />
        <Liste titel="Einsatzspezifische Hinweise (Kap. 4.2.3)" punkte={erg.hinweise} art="info" />
        {zeigeSora && (
          <div className="rounded-lg border border-dark-border bg-dark-bg-tertiary/40 p-3">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">Orientierende SORA-Einstufung (Anlage III)</h3>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm tabular-nums">
              <dt className="text-dark-text-muted">Bodenrisiko (iGRC)</dt><dd className="text-dark-text">{erg.sora.igrc ?? 'außerhalb der Tabelle'}</dd>
              <dt className="text-dark-text-muted">Nach Minderung (GRC)</dt><dd className="text-dark-text">{erg.sora.fgrc ?? '–'}</dd>
              <dt className="text-dark-text-muted">Luftrisiko (ARC)</dt><dd className="text-dark-text">ARC-{erg.sora.arc}</dd>
              <dt className="text-dark-text-muted">SAIL</dt><dd className="font-semibold text-dark-text">{erg.sora.sail ?? 'zulassungspflichtig (> 7)'}</dd>
            </dl>
            <p className="mt-2 text-xs text-dark-text-subtle">{erg.sora.arcGrund}. {erg.sora.minderung.join('; ')}.</p>
            <p className="mt-1 text-xs text-dark-text-subtle">Nur eine Orientierung für eine individuelle SORA-Bewertung, kein Ersatz dafür. EGRED 2 empfiehlt höchstens SAIL II.</p>
          </div>
        )}
        <p className="border-t border-dark-border pt-3 text-xs text-dark-text-subtle">Grundlage: {erg.grundlage}. Die Bewertung unterstützt die Entscheidung des BOS-Drohnensteuerers und der Einsatzleitung, sie ersetzt sie nicht.</p>
      </div>
    </Card>
  )
}

export function RisikobewertungPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const [e, setE] = useState<Eingaben>({ ...STANDARD })
  const setze = <K extends keyof Eingaben>(k: K) => (v: Eingaben[K]) => setE(alt => ({ ...alt, [k]: v }))
  const erg = useMemo(() => bewerte(e), [e])

  const [drohnen, setDrohnen] = useState<DrohneOption[]>([])
  // Globale Einsatzwahl (optional: ohne Einsatz ist die Bewertung allgemein); ?einsatz=<id> übernimmt der Provider
  const { einsatzIdStr: einsatzId, einsatz: gewaehlt } = useEinsatz()
  const [listenFehler, setListenFehler] = useState<string | null>(null)
  const [drohnenNeu, setDrohnenNeu] = useState(0)
  const [drohneId, setDrohneId] = useState('')
  const [gesperrt, setGesperrt] = useState<Set<GeraeteFeld>>(new Set())
  const entsperren = (f: GeraeteFeld) => () => setGesperrt(alt => { const n = new Set(alt); n.delete(f); return n })
  const drohne = drohnen.find(d => String(d.id) === drohneId) ?? null

  /** Drohne wählen: bekannte Angaben übernehmen und sperren */
  function drohneWaehlen(id: string) {
    setDrohneId(id)
    const d = drohnen.find(x => String(x.id) === id)
    const v = d ? geraeteVorgaben(d) : {}
    setE(alt => ({ ...alt, ...v }))
    setGesperrt(new Set((Object.keys(v) as Array<keyof Vorgaben>).filter((k): k is GeraeteFeld => k !== 'unter250')))
  }
  const [zweitePerson, setZweitePerson] = useState('')
  const [bemerkung, setBemerkung] = useState('')
  const [gespeichert, setGespeichert] = useState<Gespeichert[]>([])
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [gespeichertFehler, setGespeichertFehler] = useState(false)

  useEffect(() => {
    api.get('/drohnen', { params: { size: 200 } }).then(r => {
      const items = ((r.data as any)?.items ?? []) as any[]
      setDrohnen(items.map(d => ({
        id: d.id, label: [d.kennzeichen || d.asb_inventarnummer, [d.hersteller, d.modell].filter(Boolean).join(' ')].filter(Boolean).join(' – ') || `Drohne #${d.id}`,
        groesse_klasse: d.groesse_klasse ?? null, groesse_quelle: d.groesse_quelle ?? null,
        eu_klasse: d.eu_klasse ?? null, eu_klasse_quelle: d.eu_klasse_quelle ?? null,
        startmasse_g: d.startmasse_g ?? null, startmasse_quelle: d.startmasse_quelle ?? null,
        katalog_typ: d.katalog_typ ?? null, katalog_quelle: d.katalog_quelle ?? null,
      })))
      setListenFehler(null)
    }).catch(() => setListenFehler('Die Drohnenliste konnte nicht geladen werden.'))
  }, [drohnenNeu])

  const ladeListe = useCallback(() => {
    api.get('/risikobewertungen', { params: einsatzId ? { einsatz_id: einsatzId } : {} })
      .then(r => { setGespeichert(r.data as Gespeichert[]); setGespeichertFehler(false) })
      .catch(() => { setGespeichert([]); setGespeichertFehler(true) })
  }, [einsatzId])
  useEffect(() => { ladeListe() }, [ladeListe])
  useConnectionRetry(() => { ladeListe(); setDrohnenNeu(n => n + 1) })

  async function speichern() {
    setSpeichert(true); setMeldung(null)
    try {
      await api.post('/risikobewertungen', {
        einsatz_id: einsatzId ? Number(einsatzId) : null, drohne_id: drohneId ? Number(drohneId) : null,
        eingaben: e, ergebnis: erg, zweite_person: zweitePerson, bemerkung,
      })
      setMeldung({ art: 'ok', text: 'Risikobewertung gespeichert.' })
      ladeListe()
    } catch (err: any) {
      setMeldung({ art: 'fehler', text: err?.response?.data?.detail || 'Speichern fehlgeschlagen' })
    } finally { setSpeichert(false) }
  }

  async function oeffnen(id: number) {
    try {
      const r = await api.get(`/risikobewertungen/${id}`)
      const d = r.data as { eingaben: Eingaben; einsatz_id: number | null; drohne_id: number | null; zweite_person: string | null; bemerkung: string | null }
      setE({ ...STANDARD, ...d.eingaben })
      setDrohneId(d.drohne_id ? String(d.drohne_id) : '')
      setGesperrt(new Set())  // gespeicherte Eingaben bleiben unverändert und frei änderbar
      setZweitePerson(d.zweite_person ?? ''); setBemerkung(d.bemerkung ?? '')
      setMeldung({ art: 'ok', text: 'Gespeicherte Eingaben geladen. Änderungen werden als neue Bewertung gespeichert.' })
      window.scrollTo?.({ top: 0, behavior: 'smooth' })
    } catch { setMeldung({ art: 'fehler', text: 'Bewertung konnte nicht geladen werden' }) }
  }

  async function loeschen(id: number) {
    const g = gespeichert.find(x => x.id === id)
    if (!(await bestaetigen({
      titel: 'Risikobewertung löschen?', text: 'Gespeicherte Risikobewertung', objekt: g ? `${g.titel ?? '–'} (${fmtDT(g.erstellt_am)})` : `Nr. ${id}`,
      hinweis: 'Nur diese gespeicherte Bewertung wird gelöscht. Einsatz und Flüge bleiben unverändert. Das kann nicht rückgängig gemacht werden.',
    }))) return
    try { await api.delete(`/risikobewertungen/${id}`); ladeListe() }
    catch { setMeldung({ art: 'fehler', text: 'Löschen fehlgeschlagen' }) }
  }

  const dichteRelevant = e.gebiet === 'laendlich' || e.gebiet === 'urban' || e.autobahn

  return (
    <div className="space-y-5">
      {dialog}
      <p className="text-sm text-dark-text-muted">
        Einsatzbezogene Risikobewertung nach EGRED 2 mit den BOS-Standardszenarien (STS-BOS). Angaben ausfüllen, das Ergebnis aktualisiert sich sofort.
      </p>

      <Card className="p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="rb-einsatz">Einsatz</label>
            <p id="rb-einsatz" className="mt-1.5 truncate rounded-lg border border-dark-border px-3 py-2.5 text-sm text-dark-text">
              {gewaehlt ? `${gewaehlt.titel} · ${(gewaehlt.status ?? '').toLowerCase()}` : 'Ohne Einsatz'}
            </p>
            <p className="mt-1 text-xs text-dark-text-subtle">Wahl oben in der Kopfzeile.</p>
          </div>
          <div>
            <label className="label" htmlFor="rb-drohne">Drohne</label>
            <Select id="rb-drohne" value={drohneId} onChange={ev => drohneWaehlen(ev.target.value)} className="mt-1.5"
              options={[{ value: '', label: '— Nicht festgelegt —' }, ...drohnen.map(d => ({ value: String(d.id), label: d.label }))]} />
          </div>
        </div>
        {listenFehler && <p className="mt-2 text-xs text-red-300" role="alert">{listenFehler}</p>}
      </Card>

      <div className="grid items-start gap-5 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 lg:col-span-3">
          <Abschnitt nr={1} titel="Fluggerät"
            zusatz={gesperrt.size > 0 ? <span className="flex shrink-0 items-center gap-1 text-xs font-normal text-primary-300"><Lock className="h-3.5 w-3.5" />{drohne?.label.split(' – ')[0]}</span> : undefined}>
            {drohne && (
              <p className="flex items-start gap-2 rounded-lg bg-dark-bg-tertiary/50 px-3 py-2 text-xs text-dark-text-muted">
                <LockOpen className="mt-0.5 h-4 w-4 shrink-0" />
                {gesperrt.size
                  ? 'Gesperrte Angaben stammen aus den Stammdaten der Drohne bzw. dem Drohnenkatalog. Über das Schloss lassen sie sich im Einzelfall ändern.'
                  : 'Für diese Drohne sind keine Angaben hinterlegt.'}
                {(['groesse', 'klasse'] as GeraeteFeld[]).some(f => !gesperrt.has(f)) && ' Fehlende Werte bitte im Admin-Modul unter Drohnen → „Angaben für die Risikobewertung“ ergänzen.'}
              </p>
            )}
            {drohne?.katalog_quelle && (
              <p className="text-xs text-dark-text-subtle"><span className="font-medium text-dark-text-muted">Drohnenkatalog:</span> {drohne.katalog_quelle}</p>
            )}
            <Wahl label="Charakteristische Dimension" wert={e.groesse} onChange={setze('groesse')}
              gesperrt={gesperrt.has('groesse')} quelle={`Aus ${QUELLE[drohne?.groesse_quelle ?? 'drohne']}`} onEntsperren={entsperren('groesse')}
              hinweis="Größter Abstand zweier Punkte der Drohne, bei Multikoptern diagonal von Rotorspitze zu Rotorspitze."
              optionen={[{ value: 'bis1', label: 'bis 1 m' }, { value: 'bis3', label: 'über 1 m bis unter 3 m' }, { value: 'ab3', label: '3 m oder mehr' }]} />
            <Wahl label="Startmasse" wert={e.mtomAb25 ? 'ab25' : e.unter250 ? 'u250' : 'u25'}
              onChange={v => setE(alt => ({ ...alt, mtomAb25: v === 'ab25', unter250: v === 'u250' }))}
              gesperrt={gesperrt.has('mtomAb25')} onEntsperren={entsperren('mtomAb25')}
              quelle={`Aus ${QUELLE[drohne?.startmasse_quelle ?? 'drohne']}`}
              hinweis={gesperrt.has('mtomAb25') && drohne?.startmasse_g ? `${drohne.startmasse_g.toLocaleString('de-DE')} g laut ${QUELLE[drohne.startmasse_quelle ?? 'drohne']}` : undefined}
              optionen={[{ value: 'u250', label: 'unter 250 g' }, { value: 'u25', label: '250 g bis unter 25 kg' }, { value: 'ab25', label: '25 kg oder mehr' }]} />
            <Wahl label="EU-Klassenkennzeichnung" wert={e.klasse} onChange={setze('klasse')}
              hinweis="Nur für die Prüfung der offenen Kategorie."
              gesperrt={gesperrt.has('klasse')} quelle={`Aus ${QUELLE[drohne?.eu_klasse_quelle ?? 'drohne']}`} onEntsperren={entsperren('klasse')}
              optionen={[{ value: 'C0', label: 'C0' }, { value: 'C1', label: 'C1' }, { value: 'C2', label: 'C2' },
                { value: 'C3', label: 'C3' }, { value: 'C4', label: 'C4' }, { value: 'C5', label: 'C5' }, { value: 'C6', label: 'C6' },
                { value: 'keine', label: 'Keine / Bestandsdrohne' }]} />
            <Haken label="Fest stationierte Drohne (Hangar / Docking Station)" wert={e.hangar} onChange={setze('hangar')}
              gesperrt={gesperrt.has('hangar')} quelle={`Laut Drohnenkatalog (${drohne?.katalog_typ ?? ''})`} onEntsperren={entsperren('hangar')} />
          </Abschnitt>

          <Abschnitt nr={2} titel="Flugbetrieb">
            <Wahl label="Sichtverhältnis" wert={e.sicht} onChange={setze('sicht')}
              optionen={[{ value: 'VLOS', label: 'VLoS', info: 'in Sichtweite des Piloten' },
                { value: 'EVLOS', label: 'EVLoS', info: 'erweiterte Sichtweite mit Luftraumbeobachter' },
                { value: 'BVLOS', label: 'BVLoS', info: 'außerhalb der Sichtweite' }]} />
            <div>
              <label className="label" htmlFor="rb-hoehe">Maximale Flughöhe über Grund (m)</label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Input id="rb-hoehe" type="number" min={1} max={500} value={String(e.hoehe)} className="w-28"
                  onChange={ev => setze('hoehe')(Math.max(0, Number(ev.target.value) || 0))} />
                {[30, 50, 70, 100, 120].map(h => (
                  <button key={h} type="button" onClick={() => setze('hoehe')(h)} aria-pressed={e.hoehe === h}
                    className={`h-11 min-w-[48px] rounded-lg border px-2 text-sm ${e.hoehe === h ? 'border-primary-500 bg-primary-600/15 text-dark-text' : 'border-dark-border text-dark-text-muted hover:text-dark-text'}`}>{h}</button>
                ))}
              </div>
            </div>
            <Haken label="Flug bei Nacht" wert={e.nacht} onChange={setze('nacht')} />
            {e.nacht && <Haken label="Beleuchtung der Drohne nach EGRED 2 vorhanden und eingeschaltet" wert={e.beleuchtung} onChange={setze('beleuchtung')} />}
            <Haken label="Mehrere Drohnen gleichzeitig im Einsatz" wert={e.mehrereDrohnen} onChange={setze('mehrereDrohnen')} />
          </Abschnitt>

          <Abschnitt nr={3} titel="Einsatzgebiet (Bodenrisiko)">
            <Wahl label="Wo wird geflogen?" wert={e.gebiet} onChange={setze('gebiet')}
              optionen={[
                { value: 'einsatzstelle', label: 'Über der Einsatzstelle', info: 'abgesperrt, keine Unbeteiligten im Einsatzgebiet (BOS-01)' },
                { value: 'laendlich', label: 'Ländliches Gebiet', info: 'dünn besiedelt, keine Siedlung im Ground Risk Buffer (BOS-02)' },
                { value: 'urban', label: 'Bebautes / urbanes Gebiet', info: 'Wohn-, Gewerbe-, Industriegebiete (BOS-03)' },
                { value: 'ansammlung', label: 'Über Menschenansammlungen', info: 'so dicht, dass niemand die Menge verlassen kann' },
              ]} />
            {e.gebiet === 'einsatzstelle' && <Haken label="Einsatzstelle liegt in bebautem Gebiet" info="Für das Luftrisiko und die offene Kategorie." wert={e.bebaut} onChange={setze('bebaut')} />}
            <Haken label="Bundesautobahn im Fluggebiet (häufig gekreuzt oder überflogen)" info="Autobahnen zählen als urbanes Gebiet." wert={e.autobahn} onChange={setze('autobahn')} />
            {e.sicht === 'VLOS' && (
              <>
                <Haken label="Mindestens 30 m Abstand zu unbeteiligten Personen" info="Offene Kategorie A2." wert={e.abstand30} onChange={setze('abstand30')} />
                <Haken label="Mindestens 150 m Abstand zu Wohn-, Gewerbe-, Industrie- und Erholungsgebieten" info="Offene Kategorie A3." wert={e.abstand150} onChange={setze('abstand150')} />
              </>
            )}
          </Abschnitt>

          <Abschnitt nr={4} titel="Luftraum">
            <Wahl label="Luftraum im Fluggebiet" wert={e.luftraum} onChange={setze('luftraum')}
              hinweis="Geozonen prüfen, z. B. im dipul-Maptool oder auf der Taktischen Karte."
              optionen={[
                { value: 'G', label: 'Luftraum G', info: 'unkontrolliert, keine Kontrollzone' },
                { value: 'ctr_freigabe', label: 'Kontrollzone mit Freigabe', info: 'Tower-Freigabe oder passende NfL' },
                { value: 'ctr_ohne', label: 'Kontrollzone ohne Freigabe' },
                { value: 'edr_freigabe', label: 'Flugbeschränkungsgebiet (ED-R) mit Freigabe' },
                { value: 'edr_ohne', label: 'Flugbeschränkungsgebiet (ED-R) ohne Freigabe' },
              ]} />
            <Haken label="Heliport, Public Interest Site oder Flugplatz wird tangiert" wert={e.heliport} onChange={setze('heliport')} />
            {e.heliport && <Haken label="Absprache/Koordinierung ist erfolgt" wert={e.koordiniert} onChange={setze('koordiniert')} />}
          </Abschnitt>

          <Abschnitt nr={5} titel="Risikominderung">
            {dichteRelevant && (
              <>
                <Wahl label="Gefährdetendichte im Fluggebiet" wert={e.dichte} onChange={setze('dichte')}
                  hinweis="Tatsächlich gefährdete Personen; Personen in Gebäuden gelten als geschützt, in Fahrzeugen nicht. Einsatzkräfte zählen nicht mit."
                  optionen={[
                    { value: 'unbekannt', label: 'Nicht ermittelt' },
                    { value: 'f10', label: 'Faktor 10 unter Bevölkerungsdichte', info: '„M1 Low“, z. B. nachts in Gebäuden' },
                    { value: 'f100', label: 'Faktor 100 unter Bevölkerungsdichte', info: '„M1 Medium“, nur mit offiziellen Daten' },
                    { value: 'max30', label: 'höchstens 30 Personen/km²', info: 'ca. 0,3 Personen auf 100 × 100 m' },
                    { value: 'max300', label: 'höchstens 300 Personen/km²', info: 'max. 3 Personen auf 100 × 100 m' },
                  ]} />
                <Haken label="Gefährdetendichte vom BOS-Drohnensteuerer dokumentiert" wert={e.dichteDokumentiert} onChange={setze('dichteDokumentiert')} />
                <Haken label="Beruht auf offiziellen Daten und von einer zweiten Person bestätigt" info="Nötig für BVLoS im urbanen Gebiet und für Faktor 100." wert={e.dichteBestaetigt} onChange={setze('dichteBestaetigt')} />
              </>
            )}
            <Haken label="Rettungsmittel in 3 Minuten an der potenziellen Absturzstelle" info="Geschulte Einsatzkräfte, ca. 800 m fußläufig (Notfallplan „high“)." wert={e.rettung3min} onChange={setze('rettung3min')} />
            <Haken label="Fallschirm oder andere Aufprallminderung vorhanden" wert={e.fallschirm} onChange={setze('fallschirm')} />
          </Abschnitt>

          <Abschnitt nr={6} titel="Voraussetzungen der Standardszenarien">
            <Haken label="Einsatzkräfte eingewiesen" info="„Flug von Drohnen über der Einsatzstelle im Abschnitt … Verhalten im Notfall …“" wert={e.einweisung} onChange={setze('einweisung')}>
              <a href={EINWEISUNG_PDF} target="_blank" rel="noopener" className={knopf}><ExternalLink className="h-4 w-4" />Sicherheitseinweisung anzeigen</a>
              <button type="button" onClick={() => pdfDrucken(EINWEISUNG_PDF)} className={knopf}><Printer className="h-4 w-4" />Drucken</button>
            </Haken>
            <Haken label="Fluggebiet und Grenzabstand bestimmt und dokumentiert" info="LBA-Leitfaden oder dipul-Volumenplaner." wert={e.fluggebiet} onChange={setze('fluggebiet')} />
            <Haken label="Betriebshandbuch für das Szenario vorhanden" wert={e.betriebshandbuch} onChange={setze('betriebshandbuch')} />
            <Haken label="BOS-Drohnensteuerer im Ausbildungsmodul STS-BOS geschult" wert={e.ausbildung} onChange={setze('ausbildung')} />
          </Abschnitt>

          <Abschnitt nr={7} titel="Lage vor Ort (Kap. 4.2.3)">
            <Haken label="Wetter innerhalb der Herstellerangaben" info="Wind, Niederschlag, Temperatur, Sicht." wert={e.wetterOk} onChange={setze('wetterOk')} />
            <Haken label="Weitere Luftfahrzeuge im Einsatzraum" info="z. B. Rettungs- oder Polizeihubschrauber, andere Drohnen." wert={e.luftverkehr} onChange={setze('luftverkehr')} />
            <Haken label="Besondere Gefahren im Einsatzraum" info="CBRN, Explosionsgefahr, Hitze, Rauch." wert={e.gefahren} onChange={setze('gefahren')} />
            <Haken label="Erschwerende äußere Einflüsse" info="Schaulustige, Zeitdruck, unübersichtliche Einsatzstelle, Hindernisse." wert={e.externe} onChange={setze('externe')} />
            <Haken label="Einsatzauftrag, Dringlichkeit und Fähigkeit abgeglichen" wert={e.abgewogen} onChange={setze('abgewogen')} />
          </Abschnitt>
        </div>

        <div className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:col-span-2">
          <div id="rb-ergebnis" className="scroll-mt-4"><ErgebnisKarte erg={erg} /></div>

          <Card className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-dark-text">Bewertung dokumentieren</h2>
            <div>
              <label className="label" htmlFor="rb-zweite">Zweite Person (Bestätigung)</label>
              <Input id="rb-zweite" value={zweitePerson} onChange={ev => setZweitePerson(ev.target.value)} className="mt-1.5" placeholder="Name, Funktion" />
            </div>
            <div>
              <label className="label" htmlFor="rb-bem">Bemerkung / Güterabwägung</label>
              <textarea id="rb-bem" value={bemerkung} onChange={ev => setBemerkung(ev.target.value)} rows={3}
                className="input mt-1.5 w-full resize-y" placeholder="z. B. Begründung einer einsatzspezifischen Abweichung" />
            </div>
            {meldung && (
              <div className={`rounded-lg border px-3 py-2 text-sm ${meldung.art === 'ok' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-red-500/30 bg-red-500/10 text-red-200'}`}>{meldung.text}</div>
            )}
            <Button onClick={speichern} disabled={speichert} className="min-h-[48px] w-full">
              {speichert ? 'Speichere…' : 'Risikobewertung speichern'}
            </Button>
          </Card>

          <Card className="p-4">
            <h2 className="mb-2 text-sm font-semibold text-dark-text">Gespeicherte Bewertungen{einsatzId ? ' zu diesem Einsatz' : ''}</h2>
            {gespeichertFehler ? <p className="text-sm text-red-300" role="alert">Gespeicherte Bewertungen konnten nicht geladen werden.</p> : !gespeichert.length ? <p className="text-sm text-dark-text-subtle">Noch keine gespeichert.</p> : (
              <ul className="divide-y divide-dark-border">
                {gespeichert.map(g => {
                  const a = AMPEL[g.ampel ?? 'gelb']
                  const Icon = a.icon
                  return (
                    <li key={g.id} className="flex items-center gap-2 py-2">
                      <button type="button" onClick={() => oeffnen(g.id)} className="flex min-h-[44px] min-w-0 flex-1 items-start gap-2 rounded-lg px-1 text-left hover:bg-dark-bg-tertiary" title="Eingaben laden">
                        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${g.ampel === 'gruen' ? 'text-emerald-400' : g.ampel === 'rot' ? 'text-red-400' : 'text-amber-400'}`} />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-dark-text">{g.titel ?? '–'}</span>
                          <span className="block truncate text-xs text-dark-text-subtle">
                            {fmtDT(g.erstellt_am)} · {g.erstellt_von ?? '–'}{g.drohne ? ` · ${g.drohne}` : ''}{!einsatzId && g.einsatz_titel ? ` · ${g.einsatz_titel}` : ''}
                          </span>
                        </span>
                      </button>
                      <button type="button" onClick={() => loeschen(g.id)} aria-label="Bewertung löschen" title="Löschen"
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-red-400 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /></button>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {/* Handy/Tablet hochkant: Ergebnis steht unter den Fragen – feste Kurzanzeige am unteren Rand */}
      <button type="button" onClick={() => document.getElementById('rb-ergebnis')?.scrollIntoView({ behavior: 'smooth' })}
        className={`fixed inset-x-3 bottom-[calc(var(--ui-dock-h,64px)+var(--ui-safe-b,0px)+0.75rem)] z-30 flex min-h-[52px] items-center gap-3 rounded-xl border-2 px-4 py-2 text-left shadow-2xl backdrop-blur lg:hidden ${AMPEL[erg.ampel].rahmen} ${AMPEL[erg.ampel].kopf} bg-dark-bg-secondary/95`}>
        {(() => { const Icon = AMPEL[erg.ampel].icon; return <Icon className="h-5 w-5 shrink-0" /> })()}
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold uppercase tracking-wider opacity-80">{AMPEL[erg.ampel].wort}{erg.fehlend.length ? ` · ${erg.fehlend.length} offen` : ''}</span>
          <span className="block truncate text-sm font-semibold">{erg.titel}</span>
        </span>
        <span className="shrink-0 text-xs opacity-80">Ergebnis ↓</span>
      </button>
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  )
}
